import { Component, Input, OnInit } from "@angular/core";
import moment from "moment";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { ModuleManager } from "src/shared/models/module-manager";
import { ModuleManagerRefreshType } from "src/shared/models/module-manager/module-manager-options.model";
import { ScheduleAdherenceModuleData, AgentScheduleTime, Agent } from "./schedule-adherence.model";
import { ScheduleAdherenceService } from "./schedule-adherence.service";

/**
 * Schedule Adherence Widget.
 */
@Component({
    selector: "cv-schedule-adherence",
    templateUrl: "./schedule-adherence.component.html",
    styleUrls: ["./schedule-adherence.component.scss"],
})
export class ScheduleAdherenceComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    dataLoadSuccess = false;
    isDataLoading = false;
    isFirstLoad = true;
    noData = true;
    data: ScheduleAdherenceModuleData = null;
    selectedDate: Date;
    isTooMuchData = false;
    scheduleTimes: string[];
    agents: Agent[];

    tooltipLocalizedStrings = {
        schedule: '',
        in: '',
        out: '',
        minutes: ''
    };

    constructor(
        private readonly cvLocalizationService: CVLocalizationService,
        private readonly service: ScheduleAdherenceService,
    ) { }

    async ngOnInit(): Promise<void> {
        await this.loadLocalizations();
        this.initializeModuleManager();
    }

    async loadLocalizations(): Promise<void> {
        const localizationPromises = [
            this.cvLocalizationService.GetSection('Schedule_Adherence'),
        ];
        await Promise.all(localizationPromises);
        this.tooltipLocalizedStrings = {
            schedule: this.cvLocalizationService.getCachedText('Schedule_Adherence', 'Schedule'),
            in: this.cvLocalizationService.getCachedText('Schedule_Adherence', 'In_Adherence'),
            out: this.cvLocalizationService.getCachedText('Schedule_Adherence', 'Out_Adherence'),
            minutes: this.cvLocalizationService.getCachedText('Schedule_Adherence', 'Minutes'),
        };
    }

    async loadData(isAutoRefresh: boolean = false): Promise<void> {
        if (this.isDataLoading) return;

        this.isDataLoading = true;
        this.noData = false;
        this.moduleManager.showLoader = true;
        const { ScheduleAdherenceStartDate } = this.moduleManager.customSettings;
        this.selectedDate = ScheduleAdherenceStartDate ? ScheduleAdherenceStartDate.getValue() : new Date();

        try {
            this.data = await this.service.getScheduleAdherenceData(
                this.moduleKey,
                this.selectedDate,
                isAutoRefresh,
                this.isFirstLoad
            );
            this.agents = this.data.agents;
            this.scheduleTimes = this.data.xAxisLabels;
            this.noData = !this.data?.agents?.length;
            this.isTooMuchData = this.data.tooMuchData;
        } catch (error) {
            this.moduleManager.handleLoadError(error, isAutoRefresh);
            this.noData = true;
        } finally {
            this.dataLoadSuccess = !this.noData;
            this.isDataLoading = false;
            this.moduleManager.showLoader = false;
            this.isFirstLoad = false;
        }
    }

    initializeModuleManager(): void {
        this.moduleManager.setOptions({
            canRefresh: () => !this.isDataLoading,
            enableWidthClass: false,
            widgetMode: false,
            onSettingsApplied: async () => {
                await this.loadData(false);
                this.preComputeStatusMap();
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Auto,
            onRefresh: async (isAutoRefresh) => {
                if (!isAutoRefresh) this.moduleManager.showLoader = true;
                await this.loadData(isAutoRefresh);
                this.preComputeStatusMap();
            },
        });
    }

    preComputeStatusMap(): void {
        if (!this.agents) return;

        this.agents.forEach(agent => {
            for (const timeKey of Object.keys(agent.schedule)) {
                const scheduleTimeData = agent.schedule[timeKey];
                if (!scheduleTimeData) continue;

                if (!scheduleTimeData.statusMap) {
                    scheduleTimeData.statusMap = {};
                }
                const statusMap = scheduleTimeData.statusMap;

                scheduleTimeData.agentIntervalData.forEach(item => {
                    const normalized = moment(item.time).format('HH:mm');

                    if (!statusMap[normalized]) {
                        statusMap[normalized] = {
                            schedule: false,
                            in: false,
                            out: false
                        };
                    }

                    switch (item.status) {
                        case 'schedule':
                            statusMap[normalized].schedule = true;
                            break;
                        case 'in':
                            statusMap[normalized].in = true;
                            break;
                        case 'out':
                            statusMap[normalized].out = true;
                            break;
                    }
                });

                scheduleTimeData.statusMap = statusMap;
            }
        });
    };

    /**
     * trackBy functions for *ngFor to help Angular optimize rendering
     */
    trackByAgent(index: number, agent: Agent): number {
        return agent.agentID;
    }

    trackByTime(index: number, time: string): string {
        return time;
    }

    trackByMinute(index: number, minuteIso: string): string {
        return minuteIso;
    }

    computeOutPercentage(timeData: AgentScheduleTime): number {
        const total = this.getTotalScheduled(timeData);
        if (!total) return 0;
        const out = this.getOutOfAdherenceMinutes(timeData);
        return (out / total) * 100;
    }

    computeInPercentage(timeData: AgentScheduleTime): number {
        const total = this.getTotalScheduled(timeData);
        if (!total) return 0;
        const inAdherence = this.getInAdherenceMinutes(timeData);
        return (inAdherence / total) * 100;
    }

    getTotalScheduled(timeData: AgentScheduleTime): number {
        return this.getScheduledMinutes(timeData);
    }

    getScheduledMinutes(agentTimeData: AgentScheduleTime): number {
        return agentTimeData.agentIntervalData.filter(x => x.status === 'schedule').length;
    }

    getOutOfAdherenceMinutes(agentTimeData: AgentScheduleTime): number {
        return agentTimeData.agentIntervalData.filter(x => x.status === 'out').length;
    }
    getInAdherenceMinutes(timeData: AgentScheduleTime): number {
        return timeData.agentIntervalData.filter(x => x.status === 'in').length;
    }

    formatTooltip(
        timeData: AgentScheduleTime,
        type: 'in' | 'out' | 'schedule'
    ): string {
        if (!timeData?.agentIntervalData?.length) return '';

        const minutes =
            type === 'schedule'
                ? this.getScheduledMinutes(timeData)
                : type === 'out'
                    ? this.getOutOfAdherenceMinutes(timeData)
                    : this.getInAdherenceMinutes(timeData);
        const timeFormatted = moment.utc(minutes * 60 * 1000).format('HH:mm:ss');
        const percent =
            type === 'schedule'
                ? (this.getScheduledMinutes(timeData) / this.getTotalScheduled(timeData)) * 100
                : type === 'out'
                    ? this.computeOutPercentage(timeData)
                    : this.computeInPercentage(timeData);

        const start = moment(timeData.agentIntervalData[0].time).format(CVMomentTimeFormat);
        const end = moment(timeData.agentIntervalData[timeData.agentIntervalData.length - 1].time).format(CVMomentTimeFormat);
        const localizedString = this.tooltipLocalizedStrings[type];

        return `${start} - ${end}\n${percent.toFixed(2)}% / ${timeFormatted} ${this.tooltipLocalizedStrings.minutes} - ${localizedString}`;
    }

    public getOutTooltip(timeData: AgentScheduleTime): string {
        return this.formatTooltip(timeData, 'out');
    }

    public getInTooltip(timeData: AgentScheduleTime): string {
        return this.formatTooltip(timeData, 'in');
    }

    public getScheduleTooltip(timeData: AgentScheduleTime): string {
        return this.formatTooltip(timeData, 'schedule');
    }
}
